import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/kit";
import { api, isSuperadmin } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

const eur = (n) => (n == null || n === "" ? "—" : Math.round(Number(n)).toLocaleString("fr-FR") + " €");

const hasNum = (v) => v != null && v !== "" && Number(v) !== 0;
const quoteRange = (q) => {
  if (hasNum(q.ttc_min) || hasNum(q.ttc_max)) return { min: q.ttc_min, max: q.ttc_max };
  if (hasNum(q.total_min) || hasNum(q.total_max)) return { min: q.total_min, max: q.total_max };
  return { min: q.estimate_min, max: q.estimate_max };
};

export default function AdminDashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setQuotes(await api.listQuotes());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.me().then(setMe).catch(() => {}); }, []);

  const devisUrl = me?.user?.slug ? `${window.location.origin}/devis/${me.user.slug}` : "";
  const copyLink = () => {
    if (!devisUrl) return;
    navigator.clipboard?.writeText(devisUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const toggleStatus = async (e, q) => {
    e.stopPropagation();
    const newStatus = q.status === "pending" ? "reviewed" : "pending";
    const updated = await api.updateStatus(q.id, newStatus);
    setQuotes((prev) => prev.map((x) => (x.id === q.id ? updated : x)));
  };

  const pending = quotes.filter((q) => q.status === "pending").length;

  const filtered = useMemo(() => {
    return quotes.filter((q) => {
      if (filter !== "all" && q.status !== filter) return false;
      if (search && !(`${q.client_name} ${q.client_email}`.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [quotes, filter, search]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-semibold text-ink">{t("requests_title")}</h1>
          <p className="mt-1 text-[14px] text-mute">{quotes.length} {t("quotes_word")} · {pending} {t("pending_word")}</p>
        </div>
      </div>

      {devisUrl ? (
        <div data-testid="dash-public-link" className="mb-6 rounded-2xl border border-line bg-white p-4">
          <p className="text-[14px] font-semibold text-ink">{t("dash_link_title")}</p>
          <p className="mt-0.5 text-[12px] text-mute">{t("dash_link_sub")}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code data-testid="dash-public-url" className="break-all rounded-lg bg-paper-3 px-3 py-1.5 text-[13px] text-ink">{devisUrl}</code>
            <button data-testid="dash-copy-link" onClick={copyLink} className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-[13px] font-semibold text-white">
              <Icon name="copy" size={14} className="text-white" /> {copied ? t("dash_copied") : t("dash_copy")}
            </button>
          </div>
        </div>
      ) : null}

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat icon="inbox" label={t("quotes_word")} value={quotes.length} />
        <Stat icon="clock" label={t("pending_word")} value={pending} accent />
        <Stat icon="check-circle" label={t("badge_reviewed")} value={quotes.length - pending} className="hidden sm:flex" />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-xs">
          <Icon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
          <input data-testid="quote-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_ph")} className="field-input pl-9" />
        </div>
        <div className="flex gap-1 rounded-full bg-paper-3 p-1">
          {[["all", t("all_status")], ["pending", t("f_pending")], ["reviewed", t("f_reviewed")]].map(([k, lbl]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={cn("rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors", filter === k ? "bg-brand text-white" : "text-ink-3")}>{lbl}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Icon name="alert-circle" size={36} className="text-err" />
          <p className="text-[14px] text-mute">{error}</p>
          <button onClick={load} className="text-[15px] font-semibold text-brand">{t("sum_retry")}</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-white py-16 text-center">
          <Icon name="inbox" size={44} className="text-line-2" />
          <p className="text-[15px] text-mute">{t("empty_requests")}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-white">
          {/* table header (desktop) */}
          <div className="hidden grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_auto] gap-3 border-b border-divider bg-paper-3/60 px-5 py-3 text-[12px] font-semibold uppercase tracking-wide text-ink-3 lg:grid">
            <span>{t("col_client")}</span><span>{t("col_category")}</span><span>{t("col_estimate")}</span>
            <span>{t("col_status")}</span><span>{t("col_date")}</span><span />
          </div>
          {filtered.map((q) => {
            const open = expanded === q.id;
            const r = quoteRange(q);
            return (
              <div key={q.id} className="border-b border-divider last:border-0">
                <div
                  data-testid={`admin-quote-${q.id}`}
                  onClick={() => setExpanded(open ? null : q.id)}
                  className="grid cursor-pointer grid-cols-2 items-center gap-3 px-5 py-4 transition-colors hover:bg-paper-3/40 lg:grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_auto]"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-semibold text-ink">{q.client_name}</p>
                    <p className="truncate text-[12px] text-mute lg:hidden">{t(`cat_${q.category}`)}</p>
                    {isSuperadmin() && q.owner_name ? (
                      <span className="mt-0.5 inline-flex items-center gap-1 rounded bg-paper-3 px-1.5 py-0.5 text-[11px] font-medium text-ink-3">
                        <Icon name="briefcase" size={11} /> {q.owner_name}
                      </span>
                    ) : null}
                  </div>
                  <p className="hidden text-[14px] text-ink-2 lg:block">{t(`cat_${q.category}`)}</p>
                  <p className="hidden text-[14px] font-semibold text-brand lg:block">{r.min != null && r.min !== "" ? `${eur(r.min)} – ${eur(r.max)}` : "—"}</p>
                  <div className="flex justify-end lg:justify-start">
                    <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-semibold", q.status === "reviewed" ? "bg-[#EAF0EB] text-ok" : "bg-[#F5E9DC] text-warn")}>
                      {q.status === "reviewed" ? t("badge_reviewed") : t("badge_pending")}
                    </span>
                  </div>
                  <p className="hidden text-[13px] text-mute lg:block">{new Date(q.created_at).toLocaleDateString(lang, { day: "numeric", month: "short" })}</p>
                  <div className="col-span-2 flex items-center justify-end gap-2 lg:col-span-1">
                    <Icon name={open ? "chevron-up" : "chevron-down"} size={18} className="text-mute" />
                  </div>
                </div>

                {open && (
                  <div className="bg-paper-3/40 px-5 py-4">
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      <Detail label={t("d_email")} value={q.client_email} />
                      <Detail label={t("d_phone")} value={q.client_phone || "-"} />
                      <Detail label={t("d_property")} value={`${q.property_type === "house" ? t("opt_house") : t("opt_apartment")} · ${q.floor_level === "ground" ? t("opt_ground") : t("opt_upper")}`} />
                      <Detail label={t("col_estimate")} value={r.min != null && r.min !== "" ? `${eur(r.min)} – ${eur(r.max)}` : t("on_quote")} />
                    </div>
                    {(q.rooms || []).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {q.rooms.map((r, i) => {
                          const nm = r.name || `${t("room_label")} ${i + 1}`;
                          const title = r.floor ? `${nm} — ${r.floor}` : nm;
                          const dims = q.category === "flooring"
                            ? (r.floor_area ? `${r.floor_area} m²` : `${r.wall_length || "?"}×${r.wall_width || "?"} m`)
                            : `${r.wall_height || "?"}×${r.wall_length || "?"}×${r.wall_width || "?"} m`;
                          const wins = (r.windows || []).filter((w) => w.width || w.height);
                          const winTxt = wins.length ? " · " + wins.map((w) => `${w.width || "?"}×${w.height || "?"}`).join(", ") : "";
                          const doorTxt = q.category !== "flooring" ? ` · ${r.num_doors || 0} 🚪` : "";
                          return <Detail key={i} label={title} value={`${dims}${doorTxt}${winTxt}`} />;
                        })}
                      </div>
                    )}
                    {q.notes ? <Detail label={t("d_notes")} value={q.notes} /> : null}
                    {q.photos?.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-1 text-[12px] text-mute">{t("d_photos")} ({q.photos.length})</p>
                        <div className="flex gap-2 overflow-x-auto">
                          {q.photos.map((p, i) => <img key={i} src={p} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />)}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button data-testid={`toggle-status-${q.id}`} onClick={(e) => toggleStatus(e, q)} className="flex items-center gap-2 rounded-lg bg-brand-tertiary px-4 py-2 text-[14px] font-semibold text-brand-on">
                        <Icon name={q.status === "reviewed" ? "rotate-ccw" : "check"} size={16} className="text-brand" />
                        {q.status === "reviewed" ? t("mark_pending") : t("mark_reviewed")}
                      </button>
                      <button data-testid={`edit-quote-${q.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/admin/quote/${q.id}`); }} className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-[14px] font-semibold text-white">
                        <Icon name="edit-3" size={16} className="text-white" /> {t("edit_open")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon, label, value, accent, className }) => (
  <div className={cn("flex items-center gap-3 rounded-2xl border border-line bg-white p-4", className)}>
    <span className={cn("flex h-11 w-11 items-center justify-center rounded-xl", accent ? "bg-[#F5E9DC]" : "bg-brand-tertiary")}>
      <Icon name={icon} size={20} className={accent ? "text-warn" : "text-brand"} />
    </span>
    <div>
      <p className="font-display text-2xl font-bold text-ink">{value}</p>
      <p className="text-[12px] capitalize text-mute">{label}</p>
    </div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-[13px]">
    <span className="text-mute">{label}</span>
    <span className="text-right font-medium text-ink">{value}</span>
  </div>
);
